import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Alert, type CreateAlertRequest } from '@/lib/api';

export function Alerts() {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAlert, setNewAlert] = useState<CreateAlertRequest>({
    name: '',
    filter_criteria: {
      country: [],
      min_size: null,
      max_size: null,
      min_height: null,
    },
    notify_frequency: 'instant',
  });

  const { data: alerts, isLoading, error } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.alerts.list(),
  });

  const createMutation = useMutation({
    mutationFn: api.alerts.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setShowCreateForm(false);
      setNewAlert({
        name: '',
        filter_criteria: { country: [], min_size: null, max_size: null, min_height: null },
        notify_frequency: 'instant',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.alerts.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const handleCreate = () => {
    if (!newAlert.name) return;
    createMutation.mutate(newAlert);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this alert?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deal Alerts</h1>
          <p className="mt-1 text-gray-600">
            Get notified when new properties match your criteria
          </p>
        </div>
        <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
          Create Alert
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">New Alert</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Alert Name</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Large warehouses near A2"
                value={newAlert.name}
                onChange={(e) => setNewAlert((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Countries</label>
                <select
                  className="input"
                  multiple
                  value={(newAlert.filter_criteria.country as string[]) || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
                    setNewAlert((prev) => ({
                      ...prev,
                      filter_criteria: { ...prev.filter_criteria, country: selected },
                    }));
                  }}
                >
                  <option value="DE">Germany</option>
                  <option value="AT">Austria</option>
                  <option value="CH">Switzerland</option>
                  <option value="PL">Poland</option>
                </select>
              </div>
              <div>
                <label className="label">Min Size (m²)</label>
                <input
                  type="number"
                  className="input"
                  placeholder="e.g., 5000"
                  value={(newAlert.filter_criteria.min_size as number) || ''}
                  onChange={(e) =>
                    setNewAlert((prev) => ({
                      ...prev,
                      filter_criteria: {
                        ...prev.filter_criteria,
                        min_size: e.target.value ? Number(e.target.value) : null,
                      },
                    }))
                  }
                />
              </div>
              <div>
                <label className="label">Min Height (m)</label>
                <input
                  type="number"
                  step="0.5"
                  className="input"
                  placeholder="e.g., 10"
                  value={(newAlert.filter_criteria.min_height as number) || ''}
                  onChange={(e) =>
                    setNewAlert((prev) => ({
                      ...prev,
                      filter_criteria: {
                        ...prev.filter_criteria,
                        min_height: e.target.value ? Number(e.target.value) : null,
                      },
                    }))
                  }
                />
              </div>
            </div>

            <div>
              <label className="label">Notification Frequency</label>
              <select
                className="input w-auto"
                value={newAlert.notify_frequency}
                onChange={(e) =>
                  setNewAlert((prev) => ({ ...prev, notify_frequency: e.target.value }))
                }
              >
                <option value="instant">Instant</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Digest</option>
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !newAlert.name}
                className="btn btn-primary disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Alert'}
              </button>
              <button onClick={() => setShowCreateForm(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>

            {createMutation.isError && (
              <p className="text-red-500 text-sm">
                Error: {(createMutation.error as Error).message}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Alerts List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading alerts...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">
          Error loading alerts: {(error as Error).message}
        </div>
      ) : !alerts?.data?.length ? (
        <div className="card p-12 text-center">
          <p className="text-gray-500 mb-4">You haven't created any alerts yet.</p>
          <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
            Create Your First Alert
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.data.map((alert: Alert) => (
            <div key={alert.id} className="card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-gray-900">{alert.name || 'Unnamed Alert'}</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Array.isArray(alert.filter_criteria.country) && alert.filter_criteria.country.length > 0 ? (
                      <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                        Countries: {alert.filter_criteria.country.join(', ')}
                      </span>
                    ) : null}
                    {typeof alert.filter_criteria.min_size === 'number' ? (
                      <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                        Min: {alert.filter_criteria.min_size.toLocaleString()} m²
                      </span>
                    ) : null}
                    {typeof alert.filter_criteria.min_height === 'number' ? (
                      <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                        Height: &gt;={alert.filter_criteria.min_height}m
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Notifications: {alert.notify_frequency} | Created:{' '}
                    {new Date(alert.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      alert.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {alert.is_active ? 'Active' : 'Paused'}
                  </span>
                  <button
                    onClick={() => handleDelete(alert.id)}
                    disabled={deleteMutation.isPending}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
